import React from "react";
import {
  Box,
  Button,
  Grid,
  Typography,
  Chip,
  // Divider,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { toast } from "react-toastify";
import Can from "../../components/permissions/Can";
import {
  parseShouldSendContract,
  formatInvoiceMoney,
  formatSaleTypeLabel,
} from "./invoiceFieldParsers";

const labelRow = (label, value) => (
  <Grid item xs={12} sm={6}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body1" sx={{ whiteSpace: "pre-line" }}>
      {value ?? "—"}
    </Typography>
  </Grid>
);

const InvoiceUrlCopyButton = ({ url }) => {
  const handleCopy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Invoice URL copied to clipboard");
    } catch {
      toast.error("Could not copy to clipboard");
    }
  };

  return (
    <Grid item xs={12} sm={6}>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ mb: 1 }}
      >
        Invoice URL
      </Typography>
      {url ? (
        <Can permission="invoice-view">
          <Button
            variant="outlined"
            size="small"
            startIcon={<ContentCopyIcon />}
            onClick={handleCopy}
          >
            Copy invoice URL
          </Button>
        </Can>
      ) : (
        <Typography variant="body1">—</Typography>
      )}
    </Grid>
  );
};

// Services hang off the invoice's order; the invoice's own service/lead_services
// columns were dropped when invoices became order-centric.
const extractServicesForDisplay = (inv) => {
  const rawList =
    inv?.order?.services ??
    inv?.order?.order_services ??
    inv?.order?.orderServices ??
    null;

  if (!Array.isArray(rawList)) return [];

  return rawList.map((item, index) => {
    const svc = item.service ?? item;
    const name =
      (typeof svc === "object" && svc?.name) ||
      item.name ||
      (item.id
        ? `Service ${String(item.id).slice(0, 8)}…`
        : `Service ${index + 1}`);
    const price = item.amount ?? item.price ?? svc?.external_charges ?? null;
    return { id: item.id ?? svc?.id ?? index, name, price };
  });
};

const servicesLabelRow = (inv) => {
  const services = extractServicesForDisplay(inv);
  const sym = inv.currency?.symbol || inv.currency?.code || "$";

  return (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" color="text.secondary" display="block">
        Service{services.length !== 1 ? "s" : ""}
      </Typography>
      {services.length === 0 ? (
        <Typography variant="body1">—</Typography>
      ) : (
        <Box
          component="ul"
          sx={{
            m: 0,
            mt: 0.25,
            pl: 2.5,
            listStyleType: "disc",
            "& li": { display: "list-item" },
          }}
        >
          {services.map((item) => (
            <Box component="li" key={item.id} sx={{ mb: 0.25 }}>
              <Typography variant="body1" component="span">
                {item.name}
                {item.price != null && item.price !== "" ? (
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                    sx={{ ml: 0.5 }}
                  >
                    ({sym}
                    {item.price})
                  </Typography>
                ) : null}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Grid>
  );
};

const formatAllocationDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
};

const getAllocationPaymentId = (row) =>
  row.payment?.uuid ??
  row.payment?.id ??
  row.payment_uuid ??
  (row.payment_id != null ? String(row.payment_id) : null);

const AllocationsTable = ({
  allocations,
  currencySymbol = "$",
  onViewPayment,
}) => {
  const rows = Array.isArray(allocations) ? allocations : [];

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
        Allocations
      </Typography>
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No payment allocations recorded.
        </Typography>
      ) : (
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ overflowX: "auto" }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "grey.100" }}>
                <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Payment</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">
                  Amount applied
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Created at</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => {
                const paymentRouteId = getAllocationPaymentId(row);
                const paymentLabel =
                  row.payment?.transaction_id ??
                  row.payment?.reference ??
                  (paymentRouteId ? `Payment ${paymentRouteId}` : "—");

                return (
                  <TableRow key={row.uuid ?? row.id ?? index} hover>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      {paymentRouteId && onViewPayment ? (
                        <Link
                          component="button"
                          type="button"
                          variant="body2"
                          onClick={() => onViewPayment(paymentRouteId)}
                          sx={{ textAlign: "left", fontWeight: 500 }}
                        >
                          {paymentLabel}
                        </Link>
                      ) : (
                        paymentLabel
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {row.amount_applied != null && row.amount_applied !== ""
                        ? `${currencySymbol} ${row.amount_applied}`
                        : "—"}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {formatAllocationDate(row.created_at)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export const invoiceStatusChip = (inv) => {
  const s = inv?.status;
  let text = "—";
  if (s && typeof s === "object") text = s.name ?? s.label ?? s.code ?? "—";
  else if (typeof s === "string") text = s;
  else text = inv?.status_name ?? "—";
  return (
    <Chip size="small" label={text} sx={{ textTransform: "capitalize" }} />
  );
};

/** Read-only invoice fields + allocations (same as view invoice page). */
export default function InvoiceDetailContent({ inv, onViewPayment }) {
  if (!inv) return null;

  // const invoiceRef = inv.id ?? "—";

  const sym = inv.currency?.symbol || inv.currency?.code || "$";
  const formatSubAmount = (val) =>
    val != null && val !== "" ? `${sym} ${val}`.trim() : null;
  // paid / dues are derived from the invoice's payments, not stored columns.
  const paidDisplay = formatSubAmount(inv.paid_amount);
  const duesDisplay = formatSubAmount(inv.dues_amount);

  const leadName = inv.order?.lead?.name ?? inv.order?.lead?.dba_name ?? null;
  const saleType = inv.order?.sale_type;
  const noteOrDescription = inv.note ?? null;
  const invoiceUrl = inv.payment_url ?? null;
  const hasContractFlag =
    inv.should_send_contract !== undefined && inv.should_send_contract !== null;

  return (
    <>
      {/* <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        Reference
      </Typography>
      <Typography variant="h6" sx={{ mb: 2, fontFamily: 'monospace' }}>
        {invoiceRef}
      </Typography>
      <Divider sx={{ my: 2 }} /> */}
      <Grid container spacing={2}>
        {labelRow("Author", inv.author?.name ?? inv.user?.name)}
        {labelRow("Lead", leadName)}
        {labelRow(
          "Status",
          typeof inv.status === "string" ? inv.status : inv.status?.name,
        )}
        {labelRow("Created at", inv.created_at)}
        {labelRow("Amount", formatInvoiceMoney(inv))}
        {paidDisplay != null && labelRow("Paid amount", paidDisplay)}
        {duesDisplay != null && labelRow("Dues amount", duesDisplay)}
        {labelRow(
          "Payment type",
          inv.paymentType?.name ?? inv.payment_type?.name,
        )}
        {labelRow("Type of payment", inv.type_of_payment)}
        {labelRow("Currency", inv.currency?.name ?? inv.currency?.code)}
        {labelRow("Sale type", formatSaleTypeLabel(saleType))}
        {labelRow("Expires at", inv.expires_at)}
        {labelRow("Note", noteOrDescription)}
        {labelRow("Brand", inv.brand?.name)}
        {labelRow("Company", inv.company?.name)}
        {labelRow("Account", inv.account?.name)}
        <InvoiceUrlCopyButton url={invoiceUrl} />
        {servicesLabelRow(inv)}
        {hasContractFlag &&
          labelRow(
            "Should send contract",
            parseShouldSendContract(inv.should_send_contract) ? "Yes" : "No",
          )}
      </Grid>

      <AllocationsTable
        allocations={inv.allocations}
        currencySymbol={sym}
        onViewPayment={onViewPayment}
      />
    </>
  );
}
